/**
 * User Detail Modal
 *
 * Shows detailed analytics for a specific user when clicked in the table.
 */

import { useEffect, useState } from 'react';

import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

// Types
interface ModelExposureDetail {
  configId: string;
  modelName: string;
  modelKey: string;
  timesSeenAsA: number;
  timesSeenAsB: number;
  totalSeen: number;
  winsForModel: number;
  lossesForModel: number;
  ties: number;
}

interface VotingDayActivity {
  date: string;
  voteCount: number;
}

interface UserDetailData {
  email: string;
  summary: {
    totalVotes: number;
    uniquePrompts: number;
    uniqueModelsSeen: number;
    firstVote: string;
    lastVote: string;
    activitySpanDays: number;
    sources: string[];
  };
  choiceDistribution: {
    a: number;
    b: number;
    tie: number;
    bothBad: number;
  };
  votingTimeline: VotingDayActivity[];
  modelExposure: ModelExposureDetail[];
}

// Utility functions
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

// Mini bar chart for voting timeline
function VotingTimeline({ data }: { data: VotingDayActivity[] }) {
  if (data.length === 0) {
    return <div className="text-text-light text-sm">No activity data</div>;
  }

  const maxCount = Math.max(...data.map(d => d.voteCount), 1);

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-0.5 h-16">
        {data.map(day => {
          const height = (day.voteCount / maxCount) * 100;
          return (
            <div
              key={day.date}
              className="flex-1 h-full flex flex-col items-center justify-end group relative"
              style={{ minWidth: '4px', maxWidth: '12px' }}
            >
              <div
                className="w-full bg-accent/80 hover:bg-accent transition-colors rounded-t cursor-pointer"
                style={{ height: `${Math.max(height, 8)}%` }}
                title={`${formatShortDate(day.date)}: ${day.voteCount} votes`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-text-light">
        <span>{formatShortDate(data[0].date)}</span>
        <span>{formatShortDate(data[data.length - 1].date)}</span>
      </div>
    </div>
  );
}

// Choice distribution bar
function ChoiceDistribution({
  distribution,
  total,
}: {
  distribution: { a: number; b: number; tie: number; bothBad: number };
  total: number;
}) {
  const items = [
    { label: 'A wins', value: distribution.a, color: '#8B2332' },
    { label: 'B wins', value: distribution.b, color: '#2563EB' },
    { label: 'Tie', value: distribution.tie, color: '#6B7280' },
    { label: 'Both Bad', value: distribution.bothBad, color: '#9CA3AF' },
  ];

  return (
    <div className="space-y-2">
      <div className="flex h-4 rounded overflow-hidden">
        {items.map(item => {
          const percentage = total > 0 ? (item.value / total) * 100 : 0;
          if (percentage === 0) return null;
          return (
            <div
              key={item.label}
              className="h-full transition-all"
              style={{
                width: `${percentage}%`,
                backgroundColor: item.color,
              }}
              title={`${item.label}: ${item.value} (${Math.round(percentage)}%)`}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-xs">
        {items.map(item => (
          <div key={item.label} className="flex items-center gap-1">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-text-light">
              {item.label}: {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Model exposure table
function ModelExposureTable({ models }: { models: ModelExposureDetail[] }) {
  if (models.length === 0) {
    return <div className="text-text-light text-sm">No model data</div>;
  }

  return (
    <div className="max-h-64 overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="bg-cream sticky top-0">
          <tr>
            <th className="text-left py-2 px-2 text-text-light font-medium">
              Model
            </th>
            <th className="text-center py-2 px-2 text-text-light font-medium">
              Seen
            </th>
            <th className="text-center py-2 px-2 text-text-light font-medium">
              Wins
            </th>
            <th className="text-center py-2 px-2 text-text-light font-medium">
              Losses
            </th>
            <th className="text-center py-2 px-2 text-text-light font-medium">
              Win%
            </th>
          </tr>
        </thead>
        <tbody>
          {models.map(model => {
            const totalMatches =
              model.winsForModel + model.lossesForModel + model.ties;
            const winRate =
              totalMatches > 0
                ? Math.round((model.winsForModel / totalMatches) * 100)
                : 0;
            return (
              <tr key={model.configId} className="border-b border-cream-dark">
                <td className="py-2 px-2">
                  <div className="font-medium text-text">{model.modelName}</div>
                  <div className="text-xs text-text-light">
                    {model.modelKey}
                  </div>
                </td>
                <td className="text-center py-2 px-2 text-text">
                  {model.totalSeen}
                </td>
                <td className="text-center py-2 px-2 text-green-600 font-medium">
                  {model.winsForModel}
                </td>
                <td className="text-center py-2 px-2 text-red-600 font-medium">
                  {model.lossesForModel}
                </td>
                <td className="text-center py-2 px-2">
                  <span
                    className="font-semibold"
                    style={{
                      color:
                        winRate >= 50
                          ? '#059669'
                          : winRate > 0
                            ? '#DC2626'
                            : '#6B7280',
                    }}
                  >
                    {winRate}%
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Stat card
function StatCard({
  label,
  value,
  subtext,
}: {
  label: string;
  value: string | number;
  subtext?: string;
}) {
  return (
    <div className="text-center">
      <div className="text-2xl font-semibold" style={{ color: '#8B2332' }}>
        {value}
      </div>
      <div className="text-xs text-text-light">{label}</div>
      {subtext && (
        <div className="text-xs text-text-light mt-0.5">{subtext}</div>
      )}
    </div>
  );
}

// Main modal component
interface UserDetailModalProps {
  email: string;
  onClose: () => void;
}

export function UserDetailModal({ email, onClose }: UserDetailModalProps) {
  const [data, setData] = useState<UserDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const response = await apiRequest(
          `/api/admin/users/${encodeURIComponent(email)}`
        );
        if (response.success === false) {
          setError(response.error || 'User not found');
        } else {
          setData(response as UserDetailData);
        }
      } catch (e) {
        logError(
          'Failed to load user detail',
          e instanceof Error ? e : undefined
        );
        setError(
          e instanceof Error ? e.message : 'Failed to load user details'
        );
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [email]);

  // Handle escape key
  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-cream-dark">
          <div>
            <h2 className="text-lg font-semibold text-text">User Details</h2>
            <p className="text-sm text-text-light">{email}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-cream rounded-lg transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="#6B7280"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="text-center text-text-light py-12">
              Loading user details...
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          ) : data ? (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-cream rounded-lg">
                <StatCard label="Total Votes" value={data.summary.totalVotes} />
                <StatCard
                  label="Unique Prompts"
                  value={data.summary.uniquePrompts}
                />
                <StatCard
                  label="Models Seen"
                  value={data.summary.uniqueModelsSeen}
                />
                <StatCard
                  label="Active Span"
                  value={`${data.summary.activitySpanDays}d`}
                  subtext={`${formatDate(data.summary.firstVote)} - ${formatDate(data.summary.lastVote)}`}
                />
              </div>

              {/* Voting Timeline */}
              <div>
                <h3 className="text-sm font-semibold text-text mb-2">
                  Voting Activity Over Time
                </h3>
                <VotingTimeline data={data.votingTimeline} />
              </div>

              {/* Choice Distribution */}
              <div>
                <h3 className="text-sm font-semibold text-text mb-2">
                  Choice Distribution
                </h3>
                <ChoiceDistribution
                  distribution={data.choiceDistribution}
                  total={data.summary.totalVotes}
                />
              </div>

              {/* Model Exposure */}
              <div>
                <h3 className="text-sm font-semibold text-text mb-2">
                  Model Performance (from this user's votes)
                </h3>
                <p className="text-xs text-text-light mb-2">
                  How each model performed when this user voted. Win% = wins /
                  (wins + losses + ties).
                </p>
                <ModelExposureTable models={data.modelExposure} />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
